import { useEffect, useState } from 'react';
import { LocalAudioTrack, LocalVideoTrack, RemoteAudioTrack, RemoteVideoTrack } from 'twilio-video';

export type TrackType = LocalAudioTrack | LocalVideoTrack | RemoteAudioTrack | RemoteVideoTrack | undefined;

export default function useIsTrackEnabled(track: TrackType): boolean {
  const [isEnabled, setIsEnabled] = useState(track ? track.isEnabled : false);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    setIsEnabled(track ? track.isEnabled : false);

    if (track) {
      // isEnabled only reflects the enabled flag, not whether the underlying
      // MediaStreamTrack is still alive. A track that was stopped (or whose
      // source stopped sending) still reports isEnabled === true, so on its
      // own it renders a live-looking mic button over a track that transmits
      // nothing. Fold the readyState in, and resync on the source-level
      // events too, so a dead track shows as muted instead of lying.
      const sync = (): void => {
        const mediaStreamTrack = (track as { mediaStreamTrack?: MediaStreamTrack }).mediaStreamTrack;
        const isLive = !mediaStreamTrack || mediaStreamTrack.readyState === 'live';
        setIsEnabled(track.isEnabled && isLive);
      };

      sync();
      const events = ['enabled', 'disabled', 'muted', 'unmuted', 'started', 'stopped'];
      events.forEach((event) => track.on(event, sync));
      return () => {
        events.forEach((event) => track.off(event, sync));
      };
    }
  }, [track, track?.isEnabled]);

  return isEnabled;
}
